import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { PlusIcon, TruckIcon } from '@heroicons/react/24/outline';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import PageHeader from '../../components/ui/PageHeader.tsx';
import StatusChip from '../../components/ui/StatusChip.tsx';
import ConfirmationModal from '../../components/ConfirmationModal.tsx';
import StackCell from '../../components/ui/StackCell.tsx';
import OpenAction from '../../components/ui/OpenAction.tsx';
import { formatPlate } from '../../utils/licensePlate.ts';

interface FleetVehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  licensePlate: string;
  vehicleKind: string;
  fleetStatus: string;
  currentKm: number;
  driverName: string | null;
  monthKm: number;
  utilizationPct: number;
  docAlert: { level: string; label: string };
}

interface Summary {
  total: number;
  active: number;
  maintenance: number;
  monthKm: number;
  monthCost: number;
  docAlerts: number;
  expiredAlerts: number;
  expiringAlerts: number;
  periodLabel: string;
}

const statusChip = (status: string) => {
  if (status === 'active')
    return 'bg-green-100 text-green-800';
  if (status === 'maintenance')
    return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-700';
};

const statusLabel = (status: string) => {
  if (status === 'active') return 'Ativo';
  if (status === 'maintenance') return 'Em manutenção';
  return 'Inativo';
};

const docChip = (level: string) => {
  if (level === 'expired') return 'bg-red-100 text-red-800';
  if (level === 'expiring') return 'bg-amber-100 text-amber-800';
  if (level === 'missing') return 'bg-slate-100 text-slate-700';
  return 'bg-green-100 text-green-800';
};

const kindLabel = (k: string) => {
  if (k === 'moto') return 'Moto';
  if (k === 'utilitario') return 'Utilitário';
  return 'Carro';
};

const initials = (make: string, model: string) =>
  `${(make || 'V')[0]}${(model || 'X')[0]}`.toUpperCase();

const FrotaLista: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [status, setStatus] = useState('all');
  const [kind, setKind] = useState('all');
  const [docAlert, setDocAlert] = useState('any');
  const [search, setSearch] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading, error } = useQuery(
    ['fleet', status, kind, docAlert, search],
    async () => {
      const params: any = {};
      if (status !== 'all') params.status = status;
      if (kind !== 'all') params.kind = kind;
      if (docAlert === 'alert') params.docAlert = 'alert';
      if (docAlert === 'ok') params.docAlert = 'ok';
      if (search) params.search = search;
      const res = await api.get('/patrimonio/frota', { params });
      return res.data as { vehicles: FleetVehicle[]; summary: Summary };
    }
  );

  const vehicles = data?.vehicles || [];
  const summary = data?.summary;
  const isEmptyList = vehicles.length === 0;
  const canCreate = hasPermission('assets.create');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-500">Erro ao carregar a frota.</div>;
  }

  return (
    <div>
      <PageHeader
        breadcrumb="Patrimônio / Frota"
        title="Frota"
        subtitle="Veículos em uso da família/empresa · separado do Estoque p/ venda"
        actions={
          canCreate && !isEmptyList ? (
            <button
              type="button"
              data-cta="novo-veiculo"
              data-cta-placement="toolbar"
              className="btn btn-primary"
              onClick={() => navigate('/patrimonio/frota/novo')}
            >
              <PlusIcon style={{ width: 18, height: 18 }} />
              Novo veículo
            </button>
          ) : undefined
        }
      />

      <div className="dep-banner" style={{ marginBottom: 16 }}>
        <strong>Frota ≠ Estoque para venda.</strong> Aqui ficam os veículos em uso
        (família/empresa). Veículos à venda ficam em “Estoque p/ venda”.
      </div>

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Status</span>
            <select className="select" value={status} onChange={e => setStatus(e.target.value)}>
              <option value="all">Todos</option>
              <option value="active">Ativo</option>
              <option value="maintenance">Em manutenção</option>
              <option value="inactive">Inativo</option>
            </select>
          </label>
          <label className="filter-field">
            <span className="filter-label">Tipo</span>
            <select className="select" value={kind} onChange={e => setKind(e.target.value)}>
              <option value="all">Todos</option>
              <option value="carro">Carro</option>
              <option value="moto">Moto</option>
              <option value="utilitario">Utilitário</option>
            </select>
          </label>
          <label className="filter-field">
            <span className="filter-label">Alerta docs</span>
            <select className="select" value={docAlert} onChange={e => setDocAlert(e.target.value)}>
              <option value="any">Qualquer</option>
              <option value="alert">Com vencimento</option>
              <option value="ok">OK</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Placa, modelo ou condutor"
            />
          </label>
        </div>
      </div>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Veículos na frota</div>
          <div className="kpi-value">{summary?.total || 0}</div>
          <div className="kpi-hint">
            {summary?.active || 0} ativos · {summary?.maintenance || 0} em manutenção
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Km total (mês)</div>
          <div className="kpi-value">{(summary?.monthKm || 0).toLocaleString('pt-BR')} km</div>
          <div className="kpi-hint">{summary?.periodLabel}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Custos do mês</div>
          <div className="kpi-value negative">{formatCurrency(summary?.monthCost || 0)}</div>
          <div className="kpi-hint">combustível + manutenção</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Alertas de documentos</div>
          <div className="kpi-value" style={{ color: 'var(--amber-700)' }}>{summary?.docAlerts || 0}</div>
          <div className="kpi-hint">
            {summary?.expiredAlerts || 0} vencido · {summary?.expiringAlerts || 0} a vencer
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Inventário</div>
          <span className="muted">{vehicles.length} registros</span>
        </div>
        {isEmptyList ? (
          <div className="p-10 text-center" data-empty-frota="true">
            <TruckIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 mb-4">Nenhum veículo na frota ainda.</p>
            {/* Unique CTA on empty frota — toolbar above is hidden */}
            {canCreate && (
              <button
                type="button"
                data-cta="novo-veiculo"
                data-cta-placement="empty"
                onClick={() => navigate('/patrimonio/frota/novo')}
                className="btn btn-primary"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Novo veículo
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table compact-mobile">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Veículo</th>
                  <th className="text-left px-5 py-3 font-medium hide-m">Placa</th>
                  <th className="text-left px-5 py-3 font-medium hide-m">Status</th>
                  <th className="text-left px-5 py-3 font-medium hide-m">Km atual</th>
                  <th className="text-left px-5 py-3 font-medium hide-m">Utilização (mês)</th>
                  <th className="text-left px-5 py-3 font-medium hide-m">Docs / seguro</th>
                  <th className="text-right px-5 py-3 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vehicles.map(v => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <StackCell
                        initials={initials(v.make, v.model)}
                        title={`${v.make} ${v.model}`}
                        meta={`${v.year} · ${kindLabel(v.vehicleKind)}${v.driverName ? ` · ${v.driverName}` : ''}`}
                        stackExtra={
                          <>
                            {formatPlate(v.licensePlate)} · {statusLabel(v.fleetStatus)} · {v.currentKm.toLocaleString('pt-BR')} km
                            {' · '}
                            {v.monthKm.toLocaleString('pt-BR')} km/mês · {v.docAlert?.label || 'Sem documentos'}
                          </>
                        }
                      />
                    </td>
                    <td className="px-5 py-3 font-mono text-gray-800 hide-m">{formatPlate(v.licensePlate)}</td>
                    <td className="px-5 py-3 hide-m">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusChip(v.fleetStatus)}`}>
                        {statusLabel(v.fleetStatus)}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono tabular-nums hide-m">
                      {v.currentKm.toLocaleString('pt-BR')} km
                    </td>
                    <td className="px-5 py-3 hide-m">
                      <div className="font-mono text-sm">{v.monthKm.toLocaleString('pt-BR')} km</div>
                      <div className="mt-1 h-1.5 w-28 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${v.fleetStatus === 'maintenance' ? 'bg-amber-400' : 'bg-primary-500'}`}
                          style={{ width: `${v.utilizationPct || 0}%` }}
                        />
                      </div>
                    </td>
                    <td className="px-5 py-3 hide-m">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${docChip(v.docAlert?.level)}`}>
                        {v.docAlert?.label || 'Sem documentos'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="row-actions">
                        <OpenAction onClick={() => navigate(`/patrimonio/frota/${v.id}`)} />
                        {hasPermission('assets.delete') && (
                          <button
                            type="button"
                            className="link-btn action-keep action-danger"
                            onClick={() => { setDeleteId(v.id); setShowDeleteModal(true); }}
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteId(null); }}
        title="Excluir veículo da frota"
        message="Tem certeza que deseja excluir este veículo? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (deleteId == null) return;
          setDeleting(true);
          try {
            await api.delete(`/patrimonio/frota/${deleteId}`);
            queryClient.invalidateQueries(['fleet']);
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default FrotaLista;
